import { useState, useEffect } from 'react'
import { adminApi } from '../../services/api'
import { useAuth } from '../../hooks/useAuth'
import { formatCurrency, formatDate } from '../../config'
import type { Coupon, CouponUsage } from '../../types'
import LoadingSpinner from '../../components/common/LoadingSpinner'

export default function AdminCoupons() {
  const { getToken } = useAuth()
  const [coupons, setCoupons] = useState<Coupon[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [creating, setCreating] = useState(false)
  const [createForm, setCreateForm] = useState({
    code: '',
    description: '',
    discountType: 'fixed' as 'fixed' | 'percentage',
    discountValue: '',
    applicableTo: 'sponsorship_foursome' as 'sponsorship_foursome' | 'registration' | 'both',
    expiresAt: '',
  })

  // Usage modal state
  const [showUsageModal, setShowUsageModal] = useState(false)
  const [selectedCoupon, setSelectedCoupon] = useState<Coupon | null>(null)
  const [usageData, setUsageData] = useState<CouponUsage[]>([])
  const [loadingUsage, setLoadingUsage] = useState(false)

  const fetchCoupons = async () => {
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const data = await adminApi.getCoupons(token)
      setCoupons(data.coupons)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load coupons')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCoupons()
  }, [getToken])

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!createForm.code || !createForm.discountValue) {
      alert('Code and discount value are required')
      return
    }

    setCreating(true)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const discountValue = createForm.discountType === 'fixed'
        ? Math.round(parseFloat(createForm.discountValue) * 100) // Convert dollars to cents
        : parseInt(createForm.discountValue)

      await adminApi.createCoupon(token, {
        code: createForm.code,
        description: createForm.description,
        discountType: createForm.discountType,
        discountValue,
        applicableTo: createForm.applicableTo,
        expiresAt: createForm.expiresAt || null,
      })

      setShowCreateModal(false)
      setCreateForm({
        code: '',
        description: '',
        discountType: 'fixed',
        discountValue: '',
        applicableTo: 'sponsorship_foursome',
        expiresAt: '',
      })
      await fetchCoupons()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to create coupon')
    } finally {
      setCreating(false)
    }
  }

  const handleToggleActive = async (coupon: Coupon) => {
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      await adminApi.updateCoupon(token, coupon.couponId, {
        active: !coupon.active,
      })
      await fetchCoupons()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update coupon')
    }
  }

  const handleViewUsage = async (coupon: Coupon) => {
    setSelectedCoupon(coupon)
    setShowUsageModal(true)
    setLoadingUsage(true)

    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')

      const data = await adminApi.getCouponUsage(token, coupon.couponId)
      setUsageData(data.usage)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to load usage data')
    } finally {
      setLoadingUsage(false)
    }
  }

  const formatDiscount = (coupon: Coupon) => {
    if (coupon.discountType === 'fixed') {
      return formatCurrency(coupon.discountValue)
    }
    return `${coupon.discountValue}%`
  }

  const getApplicableLabel = (applicableTo: string) => {
    switch (applicableTo) {
      case 'sponsorship_foursome':
        return 'Sponsor Foursome'
      case 'registration':
        return 'Golf Registration'
      case 'both':
        return 'Both'
      default:
        return applicableTo
    }
  }

  if (loading) {
    return (
      <div className="py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <p className="text-red-600">{error}</p>
        <button onClick={fetchCoupons} className="mt-4 btn btn-primary">
          Retry
        </button>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coupons</h1>
          <p className="text-gray-600">Manage discount codes for sponsors and registrations</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary"
        >
          Create Coupon
        </button>
      </div>

      {/* Coupons Table */}
      <div className="card overflow-hidden">
        {coupons.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No coupons created yet. Click "Create Coupon" to add one.
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Code
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Discount
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Applies To
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Expires
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Uses
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {coupons.map((coupon) => (
                <tr key={coupon.couponId} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <span className="font-mono font-bold text-gray-900">
                        {coupon.code}
                      </span>
                      {coupon.description && (
                        <p className="text-sm text-gray-500">{coupon.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-green-600">
                      -{formatDiscount(coupon)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {getApplicableLabel(coupon.applicableTo)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {coupon.expiresAt ? formatDate(coupon.expiresAt) : 'Never'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleViewUsage(coupon)}
                      className="text-sla-blue hover:underline"
                    >
                      {coupon.usageCount || 0}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        coupon.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {coupon.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleActive(coupon)}
                      className={`text-sm ${
                        coupon.active
                          ? 'text-red-600 hover:text-red-800'
                          : 'text-green-600 hover:text-green-800'
                      }`}
                    >
                      {coupon.active ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-semibold mb-4">Create Coupon</h2>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Coupon Code *
                </label>
                <input
                  type="text"
                  className="input uppercase"
                  placeholder="e.g., SPONSOR150"
                  value={createForm.code}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                  }
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  className="input"
                  placeholder="Admin note (not shown to users)"
                  value={createForm.description}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, description: e.target.value }))
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Type *
                  </label>
                  <select
                    className="input"
                    value={createForm.discountType}
                    onChange={(e) =>
                      setCreateForm((prev) => ({
                        ...prev,
                        discountType: e.target.value as 'fixed' | 'percentage',
                        discountValue: '', // Reset value when type changes
                      }))
                    }
                  >
                    <option value="fixed">Fixed Amount ($)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {createForm.discountType === 'fixed' ? 'Amount ($) *' : 'Percentage (%) *'}
                  </label>
                  <input
                    type="number"
                    className="input"
                    placeholder={createForm.discountType === 'fixed' ? '150' : '25'}
                    min={createForm.discountType === 'percentage' ? 1 : 0.01}
                    max={createForm.discountType === 'percentage' ? 100 : undefined}
                    step={createForm.discountType === 'fixed' ? '0.01' : '1'}
                    value={createForm.discountValue}
                    onChange={(e) =>
                      setCreateForm((prev) => ({ ...prev, discountValue: e.target.value }))
                    }
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Applies To *
                </label>
                <select
                  className="input"
                  value={createForm.applicableTo}
                  onChange={(e) =>
                    setCreateForm((prev) => ({
                      ...prev,
                      applicableTo: e.target.value as 'sponsorship_foursome' | 'registration' | 'both',
                    }))
                  }
                >
                  <option value="sponsorship_foursome">Sponsor Foursome Only</option>
                  <option value="registration">Golf Registration Only</option>
                  <option value="both">Both</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration Date (optional)
                </label>
                <input
                  type="date"
                  className="input"
                  value={createForm.expiresAt}
                  onChange={(e) =>
                    setCreateForm((prev) => ({ ...prev, expiresAt: e.target.value }))
                  }
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" disabled={creating} className="btn btn-primary">
                  {creating ? (
                    <span className="flex items-center gap-2">
                      <LoadingSpinner size="sm" />
                      Creating...
                    </span>
                  ) : (
                    'Create Coupon'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Usage Modal */}
      {showUsageModal && selectedCoupon && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">
                Usage: <span className="font-mono">{selectedCoupon.code}</span>
              </h2>
              <p className="text-gray-600 text-sm">
                {formatDiscount(selectedCoupon)} off - {usageData.length} uses
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {loadingUsage ? (
                <div className="py-8">
                  <LoadingSpinner size="lg" />
                </div>
              ) : usageData.length === 0 ? (
                <p className="text-center text-gray-500 py-8">
                  This coupon hasn't been used yet.
                </p>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Sponsor
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Email
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Discount
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                        Date
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {usageData.map((usage) => (
                      <tr key={usage.submissionId}>
                        <td className="px-4 py-2 text-sm">{usage.sponsorName}</td>
                        <td className="px-4 py-2 text-sm text-gray-600">{usage.email}</td>
                        <td className="px-4 py-2 text-sm text-green-600">
                          -{formatCurrency(usage.discountApplied)}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">
                          {formatDate(usage.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="p-6 border-t">
              <button
                onClick={() => {
                  setShowUsageModal(false)
                  setSelectedCoupon(null)
                  setUsageData([])
                }}
                className="btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
